import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface PatientEncounterRow {
  id: string;
  org_id: string;
  clinic_id: string | null;
  patient_id: string;
  visit_id: string;
  queue_id: string | null;
  appointment_id: string | null;
  provider_id: string | null;
  type: string; // 'consultation' | 'procedure' | 'follow_up' | 'triage'
  status: string; // 'open' | 'closed'
  started_at: string | null;
  ended_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
  // Joined fields
  provider_name: string | null;
}

// ─── Queries ───

/**
 * All encounters for a patient, ordered by most recent first.
 */
export function qEncountersByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_encounters', 'by-patient', patientId] as const,
    sql: `SELECT e.*, COALESCE(p.display_name, p.full_name) AS provider_name
          FROM patient_encounters e
          LEFT JOIN appointment_providers p ON p.id = e.provider_id
          WHERE e.patient_id = ?
          ORDER BY e.started_at DESC`,
    params: [patientId],
    watch: ['patient_encounters', 'appointment_providers'],
  };
}

/**
 * Single encounter by ID.
 */
export function qEncounterById(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_encounters', 'by-id', encounterId] as const,
    sql: `SELECT e.*, COALESCE(p.display_name, p.full_name) AS provider_name
          FROM patient_encounters e
          LEFT JOIN appointment_providers p ON p.id = e.provider_id
          WHERE e.id = ?`,
    params: [encounterId],
    watch: ['patient_encounters', 'appointment_providers'],
  };
}

/**
 * Encounters for a specific visit.
 */
export function qEncountersByVisit(visitId: string): QuerySpec<[string]> {
  return {
    key: ['patient_encounters', 'by-visit', visitId] as const,
    sql: `SELECT e.*, COALESCE(p.display_name, p.full_name) AS provider_name
          FROM patient_encounters e
          LEFT JOIN appointment_providers p ON p.id = e.provider_id
          WHERE e.visit_id = ?
          ORDER BY e.started_at DESC`,
    params: [visitId],
    watch: ['patient_encounters', 'appointment_providers'],
  };
}

/**
 * Recent/open encounters for a patient (for attach-to-encounter selector).
 * Shows open encounters first, then recent closed ones (last 7 days), max 10.
 */
/**
 * Summary counts per encounter for timeline indicators.
 */
export interface EncounterSummaryCountRow {
  encounter_id: string;
  files_count: number;
  vitals_count: number;
  invoices_count: number;
  forms_count: number;
  notes_count: number;
}

export function qEncounterSummaryCounts(
  patientId: string,
): QuerySpec<[string, string, string, string]> {
  return {
    key: ['encounter-summary-counts', patientId] as const,
    sql: `
      SELECT e.id AS encounter_id,
        (SELECT COUNT(*) FROM patient_files f WHERE f.encounter_id = e.id) AS files_count,
        (SELECT COUNT(*) FROM patient_vital_entries v WHERE v.encounter_id = e.id AND v.deleted_at IS NULL) AS vitals_count,
        (SELECT COUNT(*) FROM invoices i WHERE i.encounter_id = e.id) AS invoices_count,
        (SELECT COUNT(*) FROM form_instances fi WHERE fi.encounter_id = e.id) AS forms_count,
        (SELECT COUNT(*) FROM patient_notes n WHERE n.encounter_id = e.id) AS notes_count
      FROM patient_encounters e
      WHERE e.patient_id = ?
    `,
    params: [patientId, patientId, patientId, patientId],
    watch: [
      'patient_encounters',
      'patient_files',
      'patient_vital_entries',
      'invoices',
      'form_instances',
      'patient_notes',
    ],
  };
}

/**
 * Latest vitals snapshot per encounter (systolic, diastolic, pulse).
 */
export interface EncounterVitalsSnapshotRow {
  encounter_id: string;
  code: string;
  value_number: number | null;
  unit: string | null;
}

/**
 * Latest note per encounter for timeline preview.
 * Returns one row per encounter (the most recent note).
 */
export interface EncounterNotePreviewRow {
  encounter_id: string;
  title: string | null;
  body: string | null;
  note_type: string | null;
  alert_level: string | null;
  visibility: string | null;
  author_name: string | null;
  created_at: string | null;
}

export function qEncounterNotesPreviews(patientId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-notes-previews', patientId] as const,
    sql: `
      SELECT n.encounter_id, n.title, n.body, n.note_type, n.alert_level, n.visibility, n.author_name, n.created_at
      FROM patient_notes n
      WHERE n.patient_id = ?
        AND n.encounter_id IS NOT NULL
      ORDER BY n.created_at DESC
    `,
    params: [patientId],
    watch: ['patient_notes'],
  };
}

/**
 * All latest vitals per encounter (full detail, not just BP/pulse).
 * Returns the most recent recording session per encounter with all vital entries.
 */
export interface EncounterVitalsDetailRow {
  encounter_id: string;
  name: string;
  code: string;
  value_number: number | null;
  value_text: string | null;
  unit: string | null;
  sort_order: number;
  recorded_at: string;
}

export function qEncounterVitalsDetails(patientId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-vitals-details', patientId] as const,
    sql: `
      SELECT v.encounter_id, d.name, d.code, v.value_number, v.value_text, d.unit, d.sort_order, v.recorded_at
      FROM patient_vital_entries v
      JOIN vital_definitions d ON d.id = v.vital_definition_id
      WHERE v.patient_id = ?
        AND v.encounter_id IS NOT NULL
        AND v.deleted_at IS NULL
        AND d.active = 1 AND d.deleted_at IS NULL
        AND v.recorded_at = (
          SELECT MAX(v2.recorded_at)
          FROM patient_vital_entries v2
          WHERE v2.encounter_id = v.encounter_id AND v2.deleted_at IS NULL
        )
      ORDER BY v.encounter_id, d.sort_order ASC
    `,
    params: [patientId],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

export function qEncounterVitalsSnapshot(patientId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-vitals-snapshot', patientId] as const,
    sql: `
      SELECT v.encounter_id, d.code, v.value_number, d.unit
      FROM patient_vital_entries v
      JOIN vital_definitions d ON d.id = v.vital_definition_id
      WHERE v.patient_id = ?
        AND v.encounter_id IS NOT NULL
        AND v.deleted_at IS NULL
        AND d.code IN ('systolic', 'diastolic', 'pulse')
        AND v.recorded_at = (
          SELECT MAX(v2.recorded_at)
          FROM patient_vital_entries v2
          WHERE v2.encounter_id = v.encounter_id AND v2.deleted_at IS NULL
        )
      ORDER BY v.encounter_id, d.sort_order
    `,
    params: [patientId],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

/**
 * All files per encounter for a patient (for timeline file filter).
 * Returns up to 5 files per encounter, most recent first.
 */
export interface EncounterFileDetailRow {
  encounter_id: string;
  id: string;
  filename: string;
  url: string | null;
  tag: string | null;
  mime_type: string | null;
  kind: string | null;
  created_at: string | null;
}

export function qEncounterFilesDetails(patientId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-files-details', patientId] as const,
    sql: `
      SELECT f.encounter_id, f.id, f.filename, f.url, f.tag, f.mime_type, f.kind, f.created_at
      FROM patient_files f
      WHERE f.patient_id = ?
        AND f.encounter_id IS NOT NULL
      ORDER BY f.encounter_id, f.created_at DESC
    `,
    params: [patientId],
    watch: ['patient_files'],
  };
}

/**
 * Top 3 recent files for a specific encounter.
 */
export interface EncounterFilePreviewRow {
  id: string;
  filename: string;
  tag: string | null;
  created_at: string | null;
}

export function qEncounterTopFiles(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-top-files', encounterId] as const,
    sql: `SELECT id, filename, tag, created_at FROM patient_files WHERE encounter_id = ? ORDER BY created_at DESC LIMIT 3`,
    params: [encounterId],
    watch: ['patient_files'],
  };
}

/**
 * Latest invoice for a specific encounter.
 */
export interface EncounterLatestInvoiceRow {
  id: string;
  invoice_no: string | null;
  status: string | null;
  grand_total: number | null;
  date_iso: string | null;
}

export function qEncounterLatestInvoice(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-latest-invoice', encounterId] as const,
    sql: `SELECT id, invoice_no, status, grand_total, date_iso FROM invoices WHERE encounter_id = ? ORDER BY created_at DESC LIMIT 1`,
    params: [encounterId],
    watch: ['invoices'],
  };
}

/**
 * Form instance previews per encounter for timeline body rendering.
 * Returns all forms for a patient grouped by encounter, ordered newest first.
 */
export interface EncounterFormPreviewRow {
  encounter_id: string;
  id: string;
  form_name: string | null;
  status: string;
  completed_at: string | null;
  created_at: string;
}

export function qEncounterFormsPreviews(patientId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-forms-previews', patientId] as const,
    sql: `
      SELECT fi.encounter_id, fi.id, fd.name AS form_name, fi.status, fi.completed_at, fi.created_at
      FROM form_instances fi
      LEFT JOIN form_definitions fd ON fd.id = fi.form_definition_id
      WHERE fi.patient_id = ?
        AND fi.encounter_id IS NOT NULL
      ORDER BY fi.encounter_id, fi.created_at DESC
    `,
    params: [patientId],
    watch: ['form_instances', 'form_definitions'],
  };
}

/**
 * Invoice previews per encounter for timeline body rendering.
 * Returns all invoices for a patient grouped by encounter, ordered newest first.
 */
export interface EncounterInvoicePreviewRow {
  encounter_id: string;
  id: string;
  invoice_no: string | null;
  status: string | null;
  grand_total: number | null;
  date_iso: string | null;
  created_at: string | null;
}

export function qEncounterInvoicesPreviews(patientId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-invoices-previews', patientId] as const,
    sql: `
      SELECT i.encounter_id, i.id, i.invoice_no, i.status, i.grand_total, i.date_iso, i.created_at
      FROM invoices i
      WHERE i.patient_id = ?
        AND i.encounter_id IS NOT NULL
      ORDER BY i.encounter_id, i.created_at DESC
    `,
    params: [patientId],
    watch: ['invoices'],
  };
}

/**
 * Line items for every invoice tied to a patient's encounters.
 * Used to surface the actual charged services in timeline invoice previews.
 */
export interface EncounterInvoiceItemRow {
  invoice_id: string;
  encounter_id: string;
  service_name: string | null;
  qty: number | null;
  sort_order: number | null;
}

export function qEncounterInvoiceItems(patientId: string): QuerySpec<[string]> {
  return {
    key: ['encounter-invoice-items', patientId] as const,
    sql: `
      SELECT ii.invoice_id, i.encounter_id, ii.service_name, ii.qty, ii.sort_order
      FROM invoice_items ii
      JOIN invoices i ON i.id = ii.invoice_id
      WHERE i.patient_id = ?
        AND i.encounter_id IS NOT NULL
      ORDER BY ii.invoice_id, ii.sort_order ASC, ii.created_at ASC
    `,
    params: [patientId],
    watch: ['invoice_items', 'invoices'],
  };
}

export function qEncountersTodayByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_encounters', 'recent-for-attach', patientId] as const,
    sql: `SELECT e.*, COALESCE(p.display_name, p.full_name) AS provider_name
          FROM patient_encounters e
          LEFT JOIN appointment_providers p ON p.id = e.provider_id
          WHERE e.patient_id = ?
          ORDER BY
            CASE WHEN e.status = 'open' THEN 0 ELSE 1 END,
            e.started_at DESC
          LIMIT 10`,
    params: [patientId],
    watch: ['patient_encounters', 'appointment_providers'],
  };
}
